import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BackgroundPattern } from '@/components/ui/background-pattern';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { 
  Loader2, 
  FileText, 
  Upload, 
  Download, 
  FileUp, 
  FolderPlus,
  Search,
  Sparkles,
  Clock,
  CheckCircle2,
  AlertCircle,
  FileSpreadsheet,
  FileCode,
  Presentation,
  Coins
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-hot-toast';
import { cn } from '@/lib/utils';
import { FullscreenProgress } from '@/components/ui/fullscreen-progress';

interface Project {
  id: number;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
  files_count: number;
  hasPitchDeck?: boolean;
  pitch_deck_id?: number;
  isPitchDeck?: boolean;
}

interface PitchDeck {
  id: number;
  name: string;
  company_name: string;
  uploaded_at: string;
  status: string;
  analysis_version_id: number;
}

interface File {
  id: number;
  name: string;
  file_type: string;
  file_path: string;
  size_bytes: number;
  content_type: string;
  is_generated: boolean;
  created_at: string;
  prompt_id?: number;
}

interface Prompt {
  id: number;
  prompt_text: string;
  response: string;
  status: string;
  created_at: string;
  generated_files: File[];
}

type FileFilter = 'all' | 'uploaded' | 'generated';

const funDataroomSteps = [
  "Creating your virtual Goldman Sachs office 🏢",
  "Teaching AI the difference between EBITDA and EBIT (it's subtle)",
  "Generating cap tables that actually add up to 100% 🧮",
  "Making financial models that won't make investors cry",
  "Creating legal docs so boring they're practically bulletproof",
  "Building pitch decks that could sell ice to penguins 🐧",
  "Crafting investor updates that hide the chaos beautifully",
  "Generating NDAs longer than your actual business plan",
  "Creating due diligence folders that scream 'we have our act together'",
  "Building term sheets that favor founders (for once) 📋",
  "Making financial projections that even you believe",
  "Creating org charts without circular reporting structures",
  "Generating compliance docs that lawyers would high-five 👨‍⚖️",
  "Building investor decks with the perfect amount of rocket ship emojis",
  "Creating data rooms so organized Marie Kondo would be proud ✨",
  "Generating executive summaries that executives actually read",
  "Building financial models with hockey stick curves (naturally)",
  "Creating board decks that make board meetings less painful",
  "Generating contracts in plain English (revolutionary concept)",
  "Almost done - adding that startup magic sauce! 🎯",
];

// Progress Steps Component
const DataroomRedesigned: React.FC = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [pitchDecks, setPitchDecks] = useState<PitchDeck[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [processingPrompt, setProcessingPrompt] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [fileFilter, setFileFilter] = useState<FileFilter>('all');
  const [credits, setCredits] = useState<number>(0);
  const [showCreditWarning, setShowCreditWarning] = useState(false);

  const fetchCredits = useCallback(async () => {
    try {
      const response = await api.get('/api/credits/balance');
      setCredits(response.data.credits);
    } catch (error) {
      console.error('Error fetching credits:', error);
    }
  }, [api]);

  const checkDataroomAccess = useCallback(async () => {
    try {
      const response = await api.get('/api/dataroom/check-access');
      return response.data;
    } catch (error) {
      console.error('Error checking dataroom access:', error);
      return { can_access: false, message: 'Failed to check access' };
    }
  }, [api]);

  const fetchPitchDecks = useCallback(async () => {
    try {
      const response = await api.get('/api/pitch-decks');
      setPitchDecks(response.data.pitch_decks);
      
      // Convert pitch decks to project format for dataroom
      const pitchDeckProjects = response.data.pitch_decks.map((deck: PitchDeck) => ({
        id: deck.id * -1, // Use negative ID to differentiate from regular projects
        name: deck.company_name,
        description: `Pitch deck for ${deck.company_name}`,
        created_at: deck.uploaded_at,
        updated_at: deck.uploaded_at,
        files_count: 0,
        pitch_deck_id: deck.id,
        isPitchDeck: true
      }));
      
      // Add pitch deck projects to projects list
      setProjects(prevProjects => {
        // Filter out any existing pitch deck projects
        const regularProjects = prevProjects.filter(p => !p.isPitchDeck);
        return [...regularProjects, ...pitchDeckProjects];
      });
    } catch (error) {
      console.error('Error fetching pitch decks:', error);
      toast.error('Failed to load pitch decks');
    }
  }, [api]);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/dataroom/projects');
      
      // Mark these as regular projects (not pitch decks)
      const regularProjects = response.data.projects.map((project: Project) => ({
        ...project,
        isPitchDeck: false
      }));
      
      setProjects(prevProjects => {
        // Keep any pitch deck projects that might have been added
        const pitchDeckProjects = prevProjects.filter(p => p.isPitchDeck);
        return [...regularProjects, ...pitchDeckProjects];
      });
      
      // Select the first project if available
      if (response.data.projects.length > 0 && !selectedProject) {
        setSelectedProject(regularProjects[0]);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
      setLoading(false);
    }
  }, [api, selectedProject]);

  const fetchFiles = useCallback(async (projectId: number) => {
    try {
      // If this is a pitch deck project (negative ID), fetch files from all related dataroom projects
      if (projectId < 0 && selectedProject?.pitch_deck_id) {
        const pitchDeckId = selectedProject.pitch_deck_id;
        
        // Find all dataroom projects related to this pitch deck
        const relatedDatarooms = projects.filter(p => !p.isPitchDeck && p.pitch_deck_id === pitchDeckId);
        
        if (relatedDatarooms.length > 0) {
          // Fetch files from all related dataroom projects
          const allFiles: File[] = [];
          for (const dataroom of relatedDatarooms) {
            try {
              const response = await api.get(`/api/dataroom/projects/${dataroom.id}/files`);
              allFiles.push(...response.data.files);
            } catch (error) {
              console.error(`Error fetching files for dataroom ${dataroom.id}:`, error);
            }
          }
          setFiles(allFiles);
        } else {
          // No dataroom projects yet, set empty files
          setFiles([]);
        }
      } else {
        // Regular project file fetching
        const response = await api.get(`/api/dataroom/projects/${projectId}/files`);
        setFiles(response.data.files);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
      toast.error('Failed to load files');
    }
  }, [api, projects, selectedProject]);

  const fetchPrompts = useCallback(async (projectId: number) => {
    try {
      // If this is a pitch deck project (negative ID), fetch prompts from all related dataroom projects
      if (projectId < 0 && selectedProject?.pitch_deck_id) {
        const pitchDeckId = selectedProject.pitch_deck_id;
        
        // Find all dataroom projects related to this pitch deck
        const relatedDatarooms = projects.filter(p => !p.isPitchDeck && p.pitch_deck_id === pitchDeckId);
        
        if (relatedDatarooms.length > 0) {
          // Fetch prompts from all related dataroom projects
          const allPrompts: Prompt[] = [];
          for (const dataroom of relatedDatarooms) {
            try {
              const response = await api.get(`/api/dataroom/projects/${dataroom.id}/prompts`);
              allPrompts.push(...response.data.prompts);
            } catch (error) {
              console.error(`Error fetching prompts for dataroom ${dataroom.id}:`, error);
            }
          }
          setPrompts(allPrompts);
        } else {
          // No dataroom projects yet, set empty prompts
          setPrompts([]);
        }
      } else {
        // Regular project prompt fetching
        const response = await api.get(`/api/dataroom/projects/${projectId}/prompts`);
        setPrompts(response.data.prompts);
      }
    } catch (error) {
      console.error('Error fetching prompts:', error);
      toast.error('Failed to load prompts');
    }
  }, [api, projects, selectedProject]);

  // Fetch projects and pitch decks on component mount
  useEffect(() => {
    fetchProjects();
    fetchPitchDecks();
    fetchCredits();
  }, [fetchProjects, fetchPitchDecks, fetchCredits]);

  // Fetch project details when a project is selected
  useEffect(() => {
    if (selectedProject) {
      fetchFiles(selectedProject.id);
      fetchPrompts(selectedProject.id);
    }
  }, [selectedProject, fetchFiles, fetchPrompts]);

  const createProject = async () => {
    if (!newProjectName.trim()) {
      toast.error('Project name is required');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/api/dataroom/projects', {
        name: newProjectName,
        description: newProjectDescription
      });
      
      setProjects([...projects, response.data.project]);
      setSelectedProject(response.data.project);
      setCreateDialogOpen(false);
      setNewProjectName('');
      setNewProjectDescription('');
      toast.success('Project created successfully');
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Failed to create project');
    } finally {
      setLoading(false);
    }
  };


  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || !event.target.files[0] || !selectedProject) {
      return;
    }

    const file = event.target.files[0];
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', getFileType(file.name));

    try {
      setLoading(true);
      const response = await api.post(`/api/dataroom/projects/${selectedProject.id}/files`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setFiles([...files, response.data.file]);
      setUploadDialogOpen(false);
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (fileId: number) => {
    try {
      const response = await api.get(`/api/dataroom/files/${fileId}/download`, {
        responseType: 'blob'
      });
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'download';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      } else {
        const file = files.find(f => f.id === fileId);
        if (file) {
          filename = file.name;
        }
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  const executePrompt = async () => {
    // Check if user has enough credits
    const accessCheck = await checkDataroomAccess();
    if (!accessCheck.can_access) {
      setShowCreditWarning(true);
      toast.error(accessCheck.message || 'Insufficient credits for data room generation');
      return;
    }

    if (!selectedProject) {
      // If no project exists, create one first
      try {
        setLoading(true);
        const response = await api.post('/api/dataroom/projects', {
          name: 'Data Room',
          description: 'Auto-generated data room'
        });
        
        setProjects(prevProjects => {
          // Keep any pitch deck projects that might exist
          const pitchDeckProjects = prevProjects.filter(p => p.isPitchDeck);
          return [...pitchDeckProjects, {...response.data.project, isPitchDeck: false}];
        });
        
        const newProject = {...response.data.project, isPitchDeck: false};
        setSelectedProject(newProject);
        
        // Now generate files for this project
        await generateFiles(newProject.id);
      } catch (error) {
        console.error('Error creating project:', error);
        toast.error('Failed to create project');
        setLoading(false);
      }
    } else {
      // If project exists, just generate files
      await generateFiles(selectedProject.id);
    }
  };
  
  const generateFiles = async (projectId: number) => {
    try {
      setProcessingPrompt(true);
      
      // If this is a pitch deck project (negative ID), handle differently
      if (projectId < 0 && selectedProject?.pitch_deck_id) {
        const pitchDeckId = selectedProject.pitch_deck_id;
        const companyName = selectedProject.name;
        
        // First, create a dataroom project for this pitch deck
        try {
          console.log(`Creating dataroom project for pitch deck ${pitchDeckId}`);
          const createProjectResponse = await api.post('/api/dataroom/projects', {
            name: `Data Room`,
            description: `Data room generated from ${companyName} pitch deck`,
            pitch_deck_id: pitchDeckId // Associate with the pitch deck
          });
          
          // Get the newly created project ID
          const newProjectId = createProjectResponse.data.project.id;
          console.log(`Created dataroom project with ID ${newProjectId}`);
          
          // Now generate files for this new project
          const response = await api.post(`/api/dataroom/projects/${newProjectId}/prompts`, {
            prompt: `Generate a complete investor data room for ${companyName} with financial model, cap table, and legal documents based on their pitch deck`,
            pitch_deck_id: pitchDeckId
          });
          
          // Update the project list
          const newProject = {
            ...createProjectResponse.data.project,
            isPitchDeck: false,
            pitch_deck_id: pitchDeckId
          };
          
          setProjects(prevProjects => [
            ...prevProjects,
            newProject
          ]);
          
          // Update selected project to the new one
          setSelectedProject(newProject);
          
          setPrompts([response.data.prompt, ...prompts]);
          fetchFiles(newProject.id);
          
          toast.success(`Data room for ${companyName} generated successfully`);
          
          // Update credits after successful generation
          fetchCredits();
        } catch (error: any) {
          console.error('Error creating dataroom project for pitch deck:', error);
          if (error.response?.status === 402) {
            setShowCreditWarning(true);
            toast.error(error.response.data.error || 'Insufficient credits');
          } else {
            toast.error('Failed to create dataroom project for pitch deck');
          }
          setProcessingPrompt(false);
          setLoading(false);
          return;
        }
      } else {
        // Regular project file generation
        const response = await api.post(`/api/dataroom/projects/${projectId}/prompts`, {
          prompt: "Generate a complete investor data room with financial model, cap table, pitch deck, and legal documents"
        });
        
        setPrompts([response.data.prompt, ...prompts]);
        fetchFiles(projectId);
        
        toast.success('Files generated successfully');
        
        // Update credits after successful generation
        fetchCredits();
      }
    } catch (error: any) {
      console.error('Error generating files:', error);
      if (error.response?.status === 402) {
        setShowCreditWarning(true);
        toast.error(error.response.data.error || 'Insufficient credits');
      } else {
        toast.error('Failed to generate files');
      }
    } finally {
      setProcessingPrompt(false);
      setLoading(false);
    }
  };

  const getFileType = (filename: string): string => {
    const extension = filename.split('.').pop()?.toLowerCase() || '';
    
    const codeExtensions = ['py', 'js', 'ts', 'jsx', 'tsx', 'html', 'css', 'json', 'yml', 'yaml', 'md'];
    const dataExtensions = ['csv', 'xlsx', 'xls', 'xml', 'sql'];
    const documentExtensions = ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'txt'];
    
    if (codeExtensions.includes(extension)) return 'code';
    if (dataExtensions.includes(extension)) return 'data';
    if (documentExtensions.includes(extension)) return 'document';
    
    return 'other';
  };

  const getFileIcon = (fileType: string) => {
    switch (fileType) {
      case 'code':
        return <FileCode className="h-5 w-5" />;
      case 'data':
        return <FileSpreadsheet className="h-5 w-5" />;
      case 'document':
        return <FileText className="h-5 w-5" />;
      default:
        return <FileText className="h-5 w-5" />;
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  // Filter files based on search and filters
  const filteredFiles = files.filter(file => {
    const matchesSearch = file.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = fileFilter === 'all' || 
      (fileFilter === 'uploaded' && !file.is_generated) ||
      (fileFilter === 'generated' && file.is_generated);
    
    return matchesSearch && matchesFilter;
  });


  return (
    <div className="min-h-screen bg-background relative">
      <BackgroundPattern />
      
      {/* Fixed Sidebar Navigation - matching analysis-nav style */}
      <div className="fixed left-0 top-[64px] h-[calc(100vh-64px)] w-64 bg-muted p-4 flex flex-col">
        <div className="mb-6">
          <h2 className="text-lg font-semibold">Investor Materials</h2>
        </div>
        
        <nav className="flex flex-col gap-2 flex-1" aria-label="Projects navigation">
          {/* Show pitch decks */}
          {pitchDecks.length > 0 && (
            <div className="space-y-1">
              {pitchDecks.map((deck) => {
                // Find the corresponding pitch deck project
                const pitchDeckProject = projects.find(p => p.isPitchDeck && p.pitch_deck_id === deck.id);
                
                return (
                  <div key={`deck-${deck.id}`}>
                    <button
                      onClick={() => {
                        if (pitchDeckProject) {
                          setSelectedProject(pitchDeckProject);
                        }
                      }}
                      className={cn(
                        "flex items-center rounded-md px-3 py-2 w-full font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-left",
                        selectedProject?.isPitchDeck && selectedProject?.pitch_deck_id === deck.id
                          ? "bg-background text-foreground shadow-sm"
                          : "hover:bg-background/50 hover:text-foreground"
                      )}
                      style={{ fontSize: '14px', fontFamily: '"Open Sans", sans-serif' }}
                    >
                      <Presentation className="mr-3 h-4 w-4 text-purple-500" />
                      <div className="flex-1 min-w-0">
                        <span className="block truncate">{deck.company_name}</span>
                        <span className="text-xs text-muted-foreground">Pitch Deck</span>
                      </div>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          
          {pitchDecks.length === 0 && (
            <div className="text-center py-6">
              <p className="text-sm text-muted-foreground mb-4">No pitch decks yet</p>
              <p className="text-xs text-muted-foreground">Upload a pitch deck first to generate data room files</p>
            </div>
          )}
        </nav>
      </div>
      
      {/* Main Content Area */}
      <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-12 text-center">
            <h1 className="text-5xl font-extrabold mb-6 tracking-tight">
              <span className="bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-400 bg-clip-text text-transparent">
                Investor Due Diligence
              </span>
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Create and manage your{' '}
              <span className="text-indigo-600 dark:text-indigo-400 font-semibold">AI-powered dataroom</span>.
              Generate all essential documents to streamline your{' '}
              <span className="text-indigo-600 dark:text-indigo-400 font-semibold">fundraising process</span>.
            </p>
          </div>

          {loading && projects.length === 0 ? (
            <div className="flex justify-center items-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
            </div>
          ) : pitchDecks.length === 0 ? (
            <Card className="bg-white/10 backdrop-blur-lg border border-white/20 max-w-2xl mx-auto">
              <CardContent className="flex flex-col items-center justify-center h-64">
                <FolderPlus className="h-16 w-16 text-gray-400 mb-4" />
                <p className="text-gray-500 dark:text-gray-400 text-center mb-4">
                  No pitch decks uploaded yet. Upload a pitch deck to get started.
                </p>
                <Button onClick={() => navigate('/home')} className="bg-black hover:bg-black/90 text-white">
                  Go to Dashboard
                </Button>
              </CardContent>
            </Card>
          ) : selectedProject ? (
            <div className="space-y-6">
              {/* Project Header */}
              <Card className="bg-white/10 backdrop-blur-lg border border-white/20">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-2xl">
                      {selectedProject.isPitchDeck ? (
                        <>
                          <Presentation className="inline-block mr-2 h-5 w-5 text-purple-500" />
                          {selectedProject.name}
                        </>
                      ) : (
                        selectedProject.name
                      )}
                    </CardTitle>
                    {selectedProject.isPitchDeck ? (
                      <CardDescription className="mt-1">
                        Data room generated from {selectedProject.name} pitch deck
                      </CardDescription>
                    ) : (
                      selectedProject.description && (
                        <CardDescription className="mt-1">
                          {selectedProject.description}
                        </CardDescription>
                      )
                    )}
                  </div>
                  {/* Only show Generate with AI and Upload buttons if no files exist and it's a pitch deck */}
                  {selectedProject.isPitchDeck && files.length === 0 && (
                    <div className="flex items-center gap-3">
                      <Button 
                        className={`bg-black hover:bg-black/90 text-white ${credits < 10 ? 'opacity-70 cursor-not-allowed' : ''}`}
                        onClick={() => executePrompt()}
                        disabled={processingPrompt || credits < 10}
                      >
                        {processingPrompt ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : credits < 10 ? (
                          <>
                            <AlertCircle className="h-4 w-4 mr-2 text-red-500" />
                            Need More Credits
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-4 w-4 mr-2" />
                            Generate with AI (10 credits)
                          </>
                        )}
                      </Button>
                      {/* <Button 
                        className="bg-black hover:bg-black/90 text-white"
                        onClick={() => setUploadDialogOpen(true)}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Upload
                      </Button> */}
                    </div>
                  )}
                </CardHeader>
              </Card>

              {/* Files Section */}
              <Card className="bg-white/10 backdrop-blur-lg border border-white/20">
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <CardTitle className="text-xl">All Files</CardTitle>
                    
                    {/* Search and Filters */}
                    <div className="flex flex-col sm:flex-row gap-2">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          placeholder="Search files..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-10 w-full sm:w-64"
                        />
                      </div>
                      
                      <div className="flex gap-2">
                        <select
                          value={fileFilter}
                          onChange={(e) => setFileFilter(e.target.value as FileFilter)}
                          className="px-3 py-2 rounded-md border bg-background text-sm"
                        >
                          <option value="all">All Sources</option>
                          <option value="uploaded">Uploaded</option>
                          <option value="generated">AI Generated</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  {filteredFiles.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-48 bg-gray-50 dark:bg-gray-800/30 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
                      <FileText className="h-12 w-12 text-gray-400 mb-4" />
                      <p className="text-gray-500 dark:text-gray-400 text-center mb-4">
                        {files.length === 0 
                          ? "No files yet. Use the buttons in the header to upload or generate files."
                          : "No files match your search criteria."}
                      </p>
                      {files.length === 0 && (
                        <div className="flex items-center">
                          <svg className="w-5 h-5 mr-2 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm text-gray-500">Use the options above</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {filteredFiles.map((file) => {
                        return (
                          <Card 
                            key={file.id}
                            className="bg-gray-50 dark:bg-gray-800/30 hover:shadow-lg transition-shadow"
                          >
                            <CardHeader className="pb-3">
                              <div className="flex items-start justify-between">
                                <div className="flex items-start space-x-3">
                                  <div className={`p-2 rounded-lg ${
                                    file.is_generated 
                                      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' 
                                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                  }`}>
                                    {getFileIcon(file.file_type)}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-medium truncate pr-2">{file.name}</h4>
                                    <div className="flex items-center gap-2 mt-1">
                                      {!file.is_generated && (
                                        <Badge 
                                          variant="outline"
                                          className="text-xs"
                                        >
                                          <Upload className="h-3 w-3 mr-1" />
                                          Uploaded
                                        </Badge>
                                      )}
                                      <span className="text-xs text-gray-500">{formatBytes(file.size_bytes)}</span>
                                    </div>
                                  </div>
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => downloadFile(file.id)}
                                  className="h-8 w-8 p-0"
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                              </div>
                            </CardHeader>

                            <CardFooter className="pt-3">
                              <div className="flex items-center text-xs text-gray-400">
                                <Clock className="h-3 w-3 mr-1" />
                                {formatDate(file.created_at)}
                              </div>
                            </CardFooter>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent AI Activity */}
              {prompts.length > 0 && (
                <Card className="bg-white/10 backdrop-blur-lg border border-white/20">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center">
                      <Clock className="h-5 w-5 mr-2 text-purple-600 dark:text-purple-400" />
                      Recent AI Activity
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {prompts.slice(0, 3).map((prompt) => (
                        <div key={prompt.id} className="flex items-start space-x-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800/30">
                          <div className={`p-2 rounded-full ${
                            prompt.status === 'completed' 
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' 
                              : prompt.status === 'failed'
                              ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                              : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400'
                          }`}>
                            {prompt.status === 'completed' ? (
                              <CheckCircle2 className="h-4 w-4" />
                            ) : prompt.status === 'failed' ? (
                              <AlertCircle className="h-4 w-4" />
                            ) : (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm line-clamp-2">{prompt.prompt_text}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {formatDate(prompt.created_at)} • {prompt.generated_files.length} files generated
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <Card className="bg-white/10 backdrop-blur-lg border border-white/20">
              <CardContent className="flex flex-col items-center justify-center h-64">
                <p className="text-gray-500 dark:text-gray-400 text-center">
                  Select a pitch deck from the list to view data room files.
                </p>
              </CardContent>
            </Card>
          )}
      </div>

      {/* Dialogs */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Dataroom Project</DialogTitle>
            <DialogDescription>
              Create a new project to organize your files and AI-generated content.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium">Project Name</label>
              <Input
                id="name"
                placeholder="Enter project name"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="description" className="text-sm font-medium">Description (Optional)</label>
              <Textarea
                id="description"
                placeholder="Enter project description"
                value={newProjectDescription}
                onChange={(e) => setNewProjectDescription(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={createProject} disabled={loading} className="bg-black hover:bg-black/90 text-white">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FolderPlus className="h-4 w-4 mr-2" />}
              Create Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload File</DialogTitle>
            <DialogDescription>
              Upload a file to your Dataroom project.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-center w-full">
              <label
                htmlFor="file-upload"
                className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 border-gray-300 dark:border-gray-500"
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <FileUp className="w-8 h-8 mb-3 text-gray-400" />
                  <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                    <span className="font-semibold">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Any file type (MAX 10MB)
                  </p>
                </div>
                <input
                  id="file-upload"
                  type="file"
                  className="hidden"
                  onChange={handleFileUpload}
                />
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credit Warning Dialog */}
      <Dialog open={showCreditWarning} onOpenChange={setShowCreditWarning}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Insufficient Credits</DialogTitle>
            <DialogDescription>
              You need 10 credits to generate a data room. Your current balance is {credits} credits.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-center">
              <div className="text-center">
                <Coins className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                <p className="text-lg font-semibold mb-2">
                  {Math.max(0, 10 - credits)} more credits needed
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Purchase additional credits to continue generating your data room.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreditWarning(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => navigate('/credits')} 
              className="bg-black hover:bg-black/90 text-white"
            >
              <Coins className="h-4 w-4 mr-2" />
              Get Credits
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Progress overlay for data room generation */}
      {processingPrompt && (
        <div className="fixed inset-0 z-50 flex">
          {/* Spacer for left navigation */}
          <div className="w-64 flex-shrink-0" />
          
          {/* Progress content area */}
          <div className="flex-1 relative">
            <FullscreenProgress
              status="processing"
              title="Generating Data Room"
              subtitle="Creating your investor-ready data room 🚀"
              steps={funDataroomSteps}
              stepDuration={3000}
              progressIncrement={{ min: 0.8, max: 2.5 }}
              progressInterval={2000}
              completionThreshold={95}
              onComplete={() => {
                setProcessingPrompt(false);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DataroomRedesigned;